import React from 'react';

interface ToggleSwitchProps {
  /**
   * Either `checked` or `enabled` is accepted as the source-of-truth
   * boolean. Historically the component shipped with `enabled`; it
   * was later renamed to `checked` to match the native input contract,
   * but several call sites (SpinWheel, Leaderboard, Builder cloaking
   * panel) still pass `enabled`. Accepting both keeps existing UI
   * working and prevents silent dead-toggle regressions.
   */
  checked?: boolean;
  enabled?: boolean;
  onChange: (checked: boolean) => void;
  /**
   * Label and description are optional. When the parent renders its
   * own label block (e.g. styled card with title + description) it
   * can omit these and render only the switch button itself.
   */
  label?: string;
  description?: string;
  disabled?: boolean;
}

export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  enabled,
  onChange,
  label,
  description,
  disabled = false,
}) => {
  // Resolve the canonical state from whichever prop the caller supplied.
  const isOn = checked ?? enabled ?? false;

  const switchButton = (
    <button
      type="button"
      role="switch"
      aria-checked={isOn}
      aria-label={label || 'Toggle'}
      disabled={disabled}
      onClick={() => !disabled && onChange(!isOn)}
      className={`
        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900
        ${isOn ? 'bg-blue-600' : 'bg-gray-700'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <span
        className={`
          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
          transition duration-200 ease-in-out
          ${isOn ? 'translate-x-5' : 'translate-x-0'}
        `}
      />
    </button>
  );

  // No label → caller is responsible for layout; render the bare switch.
  if (!label && !description) {
    return switchButton;
  }

  return (
    <div className="flex items-center justify-between">
      <div className="flex-1">
        {label && (
          <label className="block text-sm font-medium text-gray-300">
            {label}
          </label>
        )}
        {description && (
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        )}
      </div>
      {switchButton}
    </div>
  );
};
